/**
 * Collapse the staged re-plans into the workbench payload pieces, routing on
 * the SAME planEditability mode the table renders — the on-screen rule and
 * the emitted payload can't drift:
 *   - block  (not started): a start move is a START push — the backend shifts
 *     the finish with it (duration held) and authors the Start-On-or-After;
 *     a duration change is a duration_edit (applied by the backend BEFORE the
 *     push, so a moved+stretched plan lands exactly where the calendar showed)
 *   - finish (started rows, incl. a staged actual start): a FINISH push (pin)
 *   - point  (milestones): a START push moving the point
 *   - none: skipped
 * A component that matches the standing committed plan is dropped — re-sending
 * it would just write duplicate change_log rows.
 */
import type { StatusActivity, WorkbenchDatePush } from "../../../models";
import type { Staged } from "./staged";
import type { StagedPlan } from "./plan";
import { basePlanOf, planEditability } from "./plan";
import { iso10, workdaysBetween } from "./dates";

export interface PlanChanges {
  pushes: WorkbenchDatePush[];
  /** Explicit workday durations implied by stretched block plans. */
  durationEdits: Map<number, number>;
}

export function buildPlanChanges(
  stagedPlans: Record<number, StagedPlan>,
  byId: Map<number, StatusActivity>,
  stagedActuals: Record<number, Staged> = {},
  useExpectEnd: boolean = true,
): PlanChanges {
  const pushes: WorkbenchDatePush[] = [];
  const durationEdits = new Map<number, number>();
  for (const [idStr, plan] of Object.entries(stagedPlans)) {
    const id = Number(idStr);
    const act = byId.get(id);
    if (!act) continue;
    const base = basePlanOf(act);
    const staged = stagedActuals[id];
    const { mode } = planEditability(act, useExpectEnd, staged?.start, staged?.finish);

    switch (mode) {
      case "point":
        if (plan.start !== base.start) {
          pushes.push({ activity_id: id, push: "start", new_date: plan.start });
        }
        break;
      case "finish": {
        const standing = iso10(act.expect_end_date) || base.finish;
        if (plan.finish !== standing) {
          pushes.push({ activity_id: id, push: "finish", new_date: plan.finish });
        }
        break;
      }
      case "block": {
        if (plan.start !== base.start) {
          pushes.push({ activity_id: id, push: "start", new_date: plan.start });
        }
        const days = workdaysBetween(plan.start, plan.finish);
        const baseDays = Math.max(1, Math.round(act.remaining_duration));
        if (days !== baseDays) durationEdits.set(id, days);
        break;
      }
      default:
        break; // "none" — the plan can't move; nothing to emit
    }
  }
  return { pushes, durationEdits };
}
